import { useEffect, useState } from 'react';
import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router';
import { isSignInWithEmailLink, sendSignInLinkToEmail, signInWithEmailLink } from 'firebase/auth';
import { Mail, Megaphone } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { AUTH_MODE, getFirebaseAuth } from '@/lib/firebase';

export const Route = createFileRoute('/portal/signin')({ component: PortalSignIn });

/**
 * Sponsor sign-in (§4, CLAUDE.md), doing double duty.
 *
 * Without an `oobCode` in the URL this is the **request** page: a sponsor types
 * their address and Firebase emails them a link. With one, it is the
 * **completion** page that link returns to. One route rather than two because
 * the continue URL has to point somewhere, and pointing it at the page that
 * also issues links means a sponsor who mislays an email can get another
 * without going through an operator.
 *
 * Requesting a link is not the same as being granted access, and that
 * distinction is what makes this page safe to expose publicly. The
 * `dmn.sponsorId` claim comes only from an operator's invite
 * (`modules/console/sponsorUsers.ts`); this page merely lets someone who
 * already holds it prove they control the inbox. A link issued for an address
 * DMN never invited signs its holder into an account with no claim, which can
 * read nothing — see the "no claim ⇒ no data" invariant.
 *
 * Nothing here reveals whether an address has an account. Firebase's
 * `sendOobCode` returns an identical response either way (verified against the
 * emulator), and every failure below renders one generic message, so
 * `auth/admin-restricted-operation` is indistinguishable from
 * `auth/invalid-action-code`.
 */

/** Where the requesting browser stashes the address, so the same device needs no prompt. */
const EMAIL_KEY = 'dmn.portal.signInEmail';

/**
 * Failures that are about *us*, not about the address, so they can be reported
 * plainly without telling anyone whether an account exists.
 *
 * The split matters: an earlier version swallowed every error to stay
 * enumeration-safe, which meant a disabled provider or an expired API key
 * rendered a cheerful "check your inbox" for a mail that would never arrive —
 * indistinguishable, from the sponsor's side, from a link lost to spam. These
 * codes carry no information about the address, so surfacing them is free.
 */
const OPERATIONAL_ERRORS = new Set([
  'auth/operation-not-allowed', // email-link provider disabled on the project
  'auth/unauthorized-continue-uri', // continue URL is not an authorized domain
  'auth/invalid-continue-uri',
  'auth/invalid-api-key',
  'auth/network-request-failed',
  'auth/too-many-requests',
]);

type Mode =
  | { kind: 'request' }
  | { kind: 'sent'; email: string }
  | { kind: 'completing'; email: string }
  | { kind: 'need-email' }
  | { kind: 'unavailable' }
  | { kind: 'failed' };

function PortalSignIn() {
  const navigate = useNavigate();
  const { status } = useAuth();

  const href = window.location.href;
  const isLink = AUTH_MODE === 'firebase' && isSignInWithEmailLink(getFirebaseAuth(), href);

  /**
   * Decided here rather than in an effect, so arriving on a link goes straight
   * to "signing you in" with no intermediate flash of the request form.
   */
  const [mode, setMode] = useState<Mode>(() => {
    if (!isLink) return { kind: 'request' };
    const stashed = window.localStorage.getItem(EMAIL_KEY);
    return stashed ? { kind: 'completing', email: stashed } : { kind: 'need-email' };
  });
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  /**
   * The single completion path, entered either by the state initializer above
   * (same device, address stashed) or by the cross-device prompt. Every
   * `setMode` here happens after an await, so this is not the synchronous
   * cascade the lint rule guards against.
   */
  useEffect(() => {
    if (mode.kind !== 'completing') return;
    let cancelled = false;

    void (async () => {
      try {
        await signInWithEmailLink(getFirebaseAuth(), mode.email, href);
        window.localStorage.removeItem(EMAIL_KEY);
        if (!cancelled) await navigate({ to: '/portal' });
      } catch {
        if (!cancelled) setMode({ kind: 'failed' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, href, navigate]);

  // A sponsor who is already signed in has no business on the request form.
  if (!isLink && status === 'sponsor') return <Navigate to="/portal" />;

  const requestLink = async (address: string) => {
    setBusy(true);
    try {
      await sendSignInLinkToEmail(getFirebaseAuth(), address, {
        url: `${window.location.origin}/portal/signin`,
        handleCodeInApp: true,
      });
      window.localStorage.setItem(EMAIL_KEY, address);
      setMode({ kind: 'sent', email: address });
    } catch (caught) {
      const code = (caught as { code?: string }).code ?? '';
      if (OPERATIONAL_ERRORS.has(code)) {
        // Our problem, not theirs, and it says nothing about the address — so
        // saying so costs no privacy and saves them waiting for a mail that is
        // never coming.
        setMode({ kind: 'unavailable' });
      } else {
        // Anything else could distinguish an un-invited address from an invited
        // one (`auth/user-not-found`, `auth/admin-restricted-operation`), which
        // is the one thing this page must not do. Report success either way.
        setMode({ kind: 'sent', email: address });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      {AUTH_MODE !== 'firebase' ? (
        <p className="text-sm text-muted-foreground">
          Sign-in links need a real Firebase project. Set{' '}
          <code className="font-mono">VITE_DEV_SPONSOR=spo_&lt;id&gt;</code> to open the portal
          locally instead.
        </p>
      ) : mode.kind === 'completing' ? (
        <div className="flex flex-col items-center gap-3">
          <Spinner className="size-5" />
          <p className="text-sm text-muted-foreground">Signing you in…</p>
        </div>
      ) : mode.kind === 'sent' ? (
        <div className="space-y-3">
          <div className="mx-auto flex size-10 items-center justify-center rounded-full bg-muted">
            <Mail className="size-5 text-muted-foreground" />
          </div>
          <p className="text-sm">
            If <span className="font-medium">{mode.email}</span> has access, a sign-in link is on
            its way. Open it on this device and you will not need to type your address again.
          </p>
          <Button variant="outline" className="w-full" onClick={() => setMode({ kind: 'request' })}>
            Use a different address
          </Button>
        </div>
      ) : mode.kind === 'unavailable' ? (
        <div className="space-y-3">
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            We couldn’t send a sign-in link just now — this one is on us, not you. Try again in a
            moment, or ask your DearDoc contact.
          </p>
          <Button variant="outline" className="w-full" onClick={() => setMode({ kind: 'request' })}>
            Try again
          </Button>
        </div>
      ) : mode.kind === 'failed' ? (
        <div className="space-y-3">
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            That link didn’t work — it may have expired or already been used.
          </p>
          <Button className="w-full" onClick={() => setMode({ kind: 'request' })}>
            Send me a new link
          </Button>
        </div>
      ) : (
        <form
          className="space-y-3 text-left"
          onSubmit={(event) => {
            event.preventDefault();
            const address = email.trim();
            if (!address) return;
            // The same field either asks for a link or completes one, depending
            // on how we got here.
            if (mode.kind === 'need-email') setMode({ kind: 'completing', email: address });
            else void requestLink(address);
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="signin-email">
              {mode.kind === 'need-email' ? 'Confirm your email address' : 'Work email address'}
            </Label>
            <Input
              id="signin-email"
              type="email"
              required
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@sponsor.example"
            />
            {mode.kind === 'need-email' && (
              <p className="text-xs text-muted-foreground">
                You opened this link on a different device, so we need the address it was sent to.
              </p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={busy || !email.trim()}>
            {busy ? (
              <Spinner className="size-4" />
            ) : mode.kind === 'need-email' ? (
              'Continue'
            ) : (
              'Email me a sign-in link'
            )}
          </Button>
        </form>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div className="mx-auto flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          <Megaphone className="size-6" />
        </div>
        <div className="space-y-1">
          <h1 className="text-xl font-semibold">Your campaign dashboard</h1>
          <p className="text-sm text-muted-foreground">DearDoc Media Network</p>
        </div>
        {children}
        <p className="text-xs text-muted-foreground">
          No password to remember — we email you a single-use sign-in link.
        </p>
        <p className="text-xs text-muted-foreground">
          <Link to="/" className="underline">
            DearDoc team sign-in
          </Link>
        </p>
      </div>
    </div>
  );
}
